/**
 * Schémas Zod du socle – SOURCE UNIQUE des contrats d'échange API ↔ front (règle du 22/10/2026) :
 * les types TypeScript sont DÉDUITS de ces schémas (`z.infer` / `z.input`, voir api-types.ts) ; l'API valide ses
 * entrées avec les mêmes schémas ; le front peut valider ses formulaires avec eux.
 * Un métier ajoute ses schémas dans packages/shared/src/schemas/<metier>.ts (même convention).
 */
import { z } from 'zod';
import { ACTIONS } from './droits';
import { LANGUES } from './langues';
import { CONDITIONS_METEO, SOURCES_METEO, type SourceMeteo } from './meteo';
import { REPARTITION_MESSAGERIE } from './outils/messagerie';
import { MODES_WIDGET, WIDGETS_ACCUEIL, ZOOMS } from './preferences';

// ------------------------------------------------------------------ briques
export const schemaCodeFiliale = z.string().regex(/^[A-Z]{2}\d{2}$/, 'Code filiale attendu (FR01…)');
export const schemaFilialeOuToutes = z.union([schemaCodeFiliale, z.literal('ALL')]);
export const schemaPays = z.string().trim().toUpperCase().pipe(z.string().regex(/^[A-Z]{2}$/, 'Code pays ISO à 2 lettres'));
export const schemaDateIso = z.string().min(10);

/** Page de résultats (pagination par jeton opaque). */
export const schemaPage = <T extends z.ZodType>(element: T) => z.object({ elements: z.array(element), suivant: z.string().optional() });

export const CODES_ERREUR = [
  'non_authentifie', 'interdit', 'introuvable', 'validation', 'conflit', 'base_indisponible', 'interne', 'module_inactif',
  'etat_incompatible', 'depot_differe', 'service_indisponible',
  // Séparation des tâches (contrôle ISO 27001), 403 : l'utilisateur a le droit, mais il est à l'origine de l'élément et
  // l'action revient à une autre personne (ex. l'agent demandeur ne traite pas son propre incident). Code transverse,
  // distinct de « interdit » (manque de droit), que tout métier réutilise : separationTaches (api/src/core/erreurs.ts).
  'separation_taches',
  // Outils › Messagerie : application Entra absente, boîte non connectée (ou connexion révoquée), Microsoft 365
  // indisponible ou limitant les appels, pièce jointe trop volumineuse pour AGORA (ouvrir dans Outlook).
  'messagerie_non_configuree', 'messagerie_non_connectee', 'messagerie_indisponible', 'piece_trop_volumineuse',
  // Pièce jointe : dépôt temporaire remplacé après son contrôle (ETag).
  'depot_modifie',
  // Vérification d'identité récente (nouvelle connexion Microsoft) : exigée avant l'action, application Entra absente,
  // vérification refusée (autre compte, connexion trop ancienne, contexte d'authentification manquant).
  'verification_requise', 'verification_non_configuree', 'verification_refusee',
  // Trousseau : trop d'affichages de mots de passe en peu de temps (garde-fou contre l'extraction en masse).
  'trousseau_limite',
  // Scribe : plafond d'enregistrement de la semaine atteint (10 h par personne).
  'quota_scribe_atteint',
  // Agent vocal du support IT : plafond du jour de créations d'incidents atteint (garde-fou, docs/AGENT-VOCAL.md).
  'plafond_agent_vocal',
] as const;
export const schemaProbleme = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  code: z.enum(CODES_ERREUR),
  detail: z.string().optional(),
  champs: z.record(z.string(), z.string()).optional(),
});

// ------------------------------------------------------------------ visibilité à l'enregistrement
/**
 * Visibilité d'un enregistrement (en plus de la filiale et du droit « lire » sur le sous-module) :
 *  - `filiale`     : tous ceux qui lisent le sous-module dans la filiale (défaut) ;
 *  - `restreinte`  : seulement les membres d'un des `groupesLecture` (groupes AGORA), le créateur et les administrateurs ;
 *  - `personnelle` : seulement le créateur (et les administrateurs).
 * Appliquée par la base (RLS, core.enregistrement_visible) : un enregistrement invisible n'existe pas pour l'utilisateur.
 */
export const VISIBILITES = ['filiale', 'restreinte', 'personnelle'] as const;
export const schemaVisibilite = z.enum(VISIBILITES);
export const schemaGroupesLecture = z.array(z.string().regex(/^AGORA-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9_]+$/, 'Groupe AGORA attendu')).max(20);
/**
 * Règle de saisie d'une visibilité restreinte : au moins un groupe de lecture. À composer avec le schéma de création
 * ou de modification (`schema.superRefine(regleVisibilite)`) ; message = clé du dictionnaire (traduite par le front).
 */
export function regleVisibilite(valeurs: { visibilite?: (typeof VISIBILITES)[number]; groupesLecture?: string[] }, ctx: z.RefinementCtx): void {
  if (valeurs.visibilite === 'restreinte' && !valeurs.groupesLecture?.length) {
    ctx.addIssue({ code: 'custom', path: ['groupesLecture'], message: 'formulaires.erreurs.groupesLecture' });
  }
}

// ------------------------------------------------------------------ configuration et identité
export const schemaConfigFront = z.object({
  env: z.enum(['dev', 'test', 'prod']),
  apiUrl: z.string(),
  region: z.string(),
  cognito: z.object({ userPoolId: z.string(), clientId: z.string(), domaine: z.string(), fournisseurSso: z.string() }),
  connexion: z.object({ sso: z.boolean(), secoursEmail: z.boolean() }),
  version: z.string(),
  /**
   * Softphone Amazon Connect (pilote) : absent = pas de softphone, les numéros restent des liens `tel:`.
   * ccpUrl = https://<alias>.my.connect.aws/ccp-v2/ ; loginUrl = connexion SSO (application Entra SAML de Connect) ;
   * groupe = groupe Entra des agents du pilote (GROUPE_SOFTPHONE_PILOTE par défaut) ; filiale = filiale des appels
   * de l'instance (celle du journal côté serveur) ; numero = numéro de la ligne (E.164), affiché « Mon numéro » :
   * celui que les correspondants voient et rappellent.
   */
  softphone: z.object({
    ccpUrl: z.string(), region: z.string(), loginUrl: z.string().optional(), groupe: z.string().optional(), filiale: z.string().optional(),
    numero: z.string().optional(),
  }).optional(),
  /**
   * Extension Edge « AGORA Trousseau » : client Cognito de l'extension (code + PKCE, retour
   * https://<id de l'extension>.chromiumapp.org/) ; absent = extension non déployée dans cet environnement.
   */
  extension: z.object({ clientId: z.string() }).optional(),
});

export const schemaMoi = z.object({
  id: z.string(),
  email: z.string(),
  prenom: z.string(),
  nom: z.string(),
  groupes: z.array(z.string()),
  filiales: z.array(z.string()),
  admin: z.boolean(),
});

// ------------------------------------------------------------------ catalogue, accueil, activations
export const schemaDroitsNoeud = z.partialRecord(z.enum(ACTIONS), z.boolean());

export const schemaIndicateur = z.object({
  id: z.string(),
  source: z.string(),
  valeur: z.number(),
  unite: z.string().optional(),
  variation: z.number().optional(),
  route: z.string().optional(),
});
export const schemaAccueil = z.object({ filiale: schemaFilialeOuToutes, indicateurs: z.array(schemaIndicateur) });

/** Météo d'un lieu (meteo.ts) : coordonnées en degrés décimaux, arrondies au dixième par l'API. */
export const schemaDemandeMeteo = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lon: z.coerce.number().min(-180).max(180),
});
const schemaConditionMeteo = z.enum(CONDITIONS_METEO);
/** Températures en °C, vent en km/h, pluie = probabilité maximale du jour en % (null si le modèle ne la donne pas). */
export const schemaMeteo = z.object({
  lat: z.number(),
  lon: z.number(),
  actuel: z.object({ temperature: z.number(), ressenti: z.number(), vent: z.number(), condition: schemaConditionMeteo }),
  /** Aujourd'hui puis les jours suivants (dates locales du lieu, AAAA-MM-JJ). */
  jours: z.array(z.object({ date: z.string(), condition: schemaConditionMeteo, max: z.number(), min: z.number(), pluie: z.number().nullable() })),
  source: z.enum(Object.keys(SOURCES_METEO) as [SourceMeteo, ...SourceMeteo[]]),
  /** Heure de la mesure chez le fournisseur (ISO 8601) : les données ont au plus 30 min d'écart avec lui. */
  majLe: z.string(),
});

export const schemaActivations = z.object({
  parDefaut: z.boolean(),
  filiales: z.record(z.string(), z.record(z.string(), z.boolean())),
});
export const schemaReglagesActivation = z.record(z.string(), z.boolean()).refine((r) => Object.keys(r).length > 0, 'Aucun réglage');

/**
 * Administration › Droits (lecture seule) : pour chaque groupe AGORA, ses filiales (périmètre du nom), ses droits
 * (décisions Verified Permissions pour un membre de ce seul groupe, hors droits communs à tous) et ses membres (Cognito).
 * `commun` : droits de tout utilisateur AGORA, sans groupe.
 */
export const schemaDroitsParNoeud = z.record(z.string(), z.array(z.enum(ACTIONS)));
export const schemaMembreGroupe = z.object({ email: z.string(), nom: z.string(), actif: z.boolean() });
export const schemaDroitsGroupe = z.object({
  groupe: z.string(),
  filiales: z.array(z.string()),
  droits: schemaDroitsParNoeud,
  membres: z.array(schemaMembreGroupe),
});
export const schemaDroitsAdministration = z.object({
  commun: schemaDroitsParNoeud,
  groupes: z.array(schemaDroitsGroupe),
  calculeLe: schemaDateIso,
});

// ------------------------------------------------------------------ préférences
export const schemaPreferences = z.object({
  version: z.literal(1),
  langue: z.enum(LANGUES).optional(),
  theme: z.enum(['clair', 'sombre', 'systeme']),
  /** Zoom de l'affichage en % (100 = référence AGORA), réglé à part sur ordinateur et sur téléphone. */
  zoom: z.object({ bureau: z.literal(ZOOMS), mobile: z.literal(ZOOMS) }),
  filiale: schemaFilialeOuToutes.optional(),
  favoris: z.array(z.string()),
  accueil: z.object({ widgets: z.array(z.object({ id: z.enum(WIDGETS_ACCUEIL), visible: z.boolean(), mode: z.enum(MODES_WIDGET).optional() })) }),
  barreLateraleRepliee: z.boolean(),
  recents: z.array(z.object({ id: z.string(), le: z.string() })),
  /** Outils › Messagerie : bouton du dock (et écran) activés par l'utilisateur (Réglages). */
  messagerie: z.boolean(),
  /** Outils › Messagerie : part de la liste des messages (en %) face au message ouvert. */
  messagerieRepartition: z.number().int().min(REPARTITION_MESSAGERIE.min).max(REPARTITION_MESSAGERIE.max),
  /** Softphone : bouton Téléphone du dock activé par l'utilisateur (Réglages › Téléphone ; agents du pilote). */
  softphone: z.boolean(),
  /** Outils › Scribe : bouton de l'enregistreur de réunions dans le dock, activé par l'utilisateur (Réglages › Scribe). */
  scribe: z.boolean(),
});

// ------------------------------------------------------------------ dimension « tiers »
/**
 * Relation du tiers avec PRODEVAL (une seule) : un prospect n'est jamais converti, son type devient client à la
 * signature. Les rôles (plusieurs) disent sa place dans les projets (docs/COMMERCE-CADRAGE.md §3).
 */
export const TYPES_TIERS = ['prospect', 'client', 'fournisseur', 'sous-traitant', 'partenaire', 'concurrent'] as const;
export const schemaTypeTiers = z.enum(TYPES_TIERS);
export const ROLES_TIERS = [
  'client-final', 'constructeur', 'prescripteur', 'bureau-etudes', 'apporteur', 'investisseur', 'exploitant', 'gestionnaire-reseau',
] as const;
export const schemaRoleTiers = z.enum(ROLES_TIERS);
/** Activité d'un client final (origine du biogaz). */
export const ACTIVITES_TIERS = ['agricole', 'agro-industrielle', 'dechets', 'step', 'isdnd', 'industrielle', 'collectivite', 'autre'] as const;
export const schemaActiviteTiers = z.enum(ACTIVITES_TIERS);
export const schemaCodeTiers = z.string().trim().toUpperCase().pipe(z.string().regex(/^[A-Z0-9-]{2,20}$/, '2 à 20 caractères A-Z, 0-9, -'));
/** E-mail en minuscules (forme unique du socle pour les utilisateurs et les contacts). */
export const schemaEmailMinuscule = z.string().trim().toLowerCase().pipe(z.string().max(254).regex(/^[^@\s]+@[^@\s]+$/, 'E-mail attendu'));

export const schemaTiers = z.object({
  id: z.string(),
  filiale: schemaCodeFiliale,
  code: z.string(),
  raisonSociale: z.string(),
  type: schemaTypeTiers,
  pays: z.string(),
  ville: z.string().nullable(),
  actif: z.boolean(),
  visibilite: schemaVisibilite,
  groupesLecture: z.array(z.string()),
  modifieLe: schemaDateIso,
  modifiePar: z.string(),
  // Commerce (cadrage du 23/10/2026) : rôles dans les projets, identifiants, coordonnées, responsable du compte.
  roles: z.array(schemaRoleTiers),
  qualifieLe: schemaDateIso.nullable(),
  activite: schemaActiviteTiers.nullable(),
  identifiantLegal: z.string().nullable(),
  numeroTva: z.string().nullable(),
  codeX3: z.string().nullable(),
  adresse: z.string().nullable(),
  codePostal: z.string().nullable(),
  telephone: z.string().nullable(),
  siteWeb: z.string().nullable(),
  responsable: z.string().nullable(),
  tiersParentId: z.string().nullable(),
  refExterne: z.string().nullable(),
});

/** Chaîne facultative : vide ou absente → non renseignée (null en modification). */
const texte = (max: number) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), z.string().trim().max(max).nullish());

/** Coordonnées et identifiants d'un tiers, communs à la création, la modification et l'import. */
export const champsCommerceTiers = {
  roles: z.array(schemaRoleTiers).max(ROLES_TIERS.length),
  activite: z.preprocess((v) => (v === '' ? null : v), schemaActiviteTiers.nullish()),
  identifiantLegal: texte(40),
  numeroTva: z.preprocess(
    (v) => (typeof v === 'string' ? (v.replace(/[\s.\-]/g, '').toUpperCase() || null) : v),
    z.string().regex(/^[A-Z0-9]{4,20}$/, 'Numéro de TVA : 4 à 20 lettres ou chiffres').nullish(),
  ),
  codeX3: z.preprocess(
    (v) => (typeof v === 'string' ? (v.trim().toUpperCase() || null) : v),
    z.string().regex(/^[A-Z0-9-]{1,20}$/, 'Code Sage X3 : 1 à 20 caractères A-Z, 0-9, -').nullish(),
  ),
  adresse: texte(300),
  codePostal: texte(20),
  telephone: texte(40),
  siteWeb: texte(300),
  responsable: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), schemaEmailMinuscule.nullish()),
  tiersParentId: z.guid().nullish(),
  refExterne: texte(64),
};

export const schemaTiersNouveau = z.object({
  filiale: schemaCodeFiliale,
  // Absent ou vide : code attribué par la base (AG-000001…), pour un prospect sans code Sage X3.
  code: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    schemaCodeTiers.optional(),
  ),
  raisonSociale: z.string().trim().min(1, 'Obligatoire').max(200),
  type: schemaTypeTiers,
  pays: schemaPays,
  ville: texte(100),
  visibilite: schemaVisibilite.default('filiale'),
  groupesLecture: schemaGroupesLecture.default([]),
  ...champsCommerceTiers,
  roles: champsCommerceTiers.roles.default([]),
});

export const schemaTiersModification = z
  .object({
    raisonSociale: z.string().trim().min(1).max(200),
    type: schemaTypeTiers,
    pays: schemaPays,
    ville: texte(100),
    actif: z.boolean(),
    visibilite: schemaVisibilite,
    groupesLecture: schemaGroupesLecture,
    ...champsCommerceTiers,
  })
  .partial()
  .strict();

export const schemaRechercheTiers = z.object({
  filiale: z.string().optional(),
  q: z.string().trim().max(100).optional(),
  type: schemaTypeTiers.optional(),
  role: schemaRoleTiers.optional(),
  limite: z.coerce.number().int().min(1).max(200).default(50),
  suivant: z.string().optional(),
});

// ------------------------------------------------------------------ pièces jointes non documentaires
/**
 * Pièces jointes « non documentaires » (photos terrain, scans, captures) – les documents gérés restent dans SharePoint.
 * Liste blanche des types (avec leurs extensions) et taille maximale : vérifiées à la demande, par la politique du
 * dépôt S3 direct ET à la confirmation (en-tête du fichier).
 */
export const TYPES_PIECE_JOINTE = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/webp': ['webp'],
  'image/heic': ['heic'],
  'image/gif': ['gif'],
  'application/pdf': ['pdf'],
  'application/vnd.ms-outlook': ['msg'],   // message Outlook enregistré (échange avec un client, un fournisseur…)
  'message/rfc822': ['eml'],               // e-mail au format MIME (enregistré depuis Outils › Messagerie, Thunderbird…)
} as const;
export type TypePieceJointe = keyof typeof TYPES_PIECE_JOINTE;
export const TAILLE_MAX_PIECE_JOINTE = 20 * 1024 * 1024;

export const schemaTypePieceJointe = z.enum(Object.keys(TYPES_PIECE_JOINTE) as [TypePieceJointe, ...TypePieceJointe[]]);

/**
 * Plafond d'un fichier déposé par un OUTIL MCP (`agora_pieces_jointes_deposer` ; décision du DSI, Issue #285 : « même
 * règle qu'un user classique »). Un client MCP n'a pas le formulaire S3 signé du navigateur : le fichier arrive DANS
 * l'appel d'outil, encodé en base64, et traverse la Lambda de l'API. Or une invocation Lambda synchrone est limitée à
 * 6 Mo (6 291 456 octets) pour TOUT l'évènement d'API Gateway : corps JSON-RPC, en-têtes (dont le jeton d'accès) et
 * contexte de la requête (revendications du jeton).
 *  - 3 Mo de fichier = 4 194 304 caractères base64 (4 caractères pour 3 octets) : il reste ≈ 2 Mo pour le reste ;
 *  - pire cas : API Gateway transmet en plus le corps encodé en base64 (`isBase64Encoded`, géré par lireCorpsJson),
 *    soit × 4/3 ≈ 5,6 Mo : il reste ≈ 680 Ko pour l'enveloppe JSON-RPC, les en-têtes et le contexte ;
 *  - 4 Mo ne tiendraient pas dans ce pire cas (≈ 7,5 Mo).
 * Au-delà (jusqu'à TAILLE_MAX_PIECE_JOINTE), le fichier se dépose depuis l'écran (dépôt S3 direct). Toutes les autres
 * règles (liste blanche, signature, droits, visibilité, audit) sont celles de l'écran.
 */
export const TAILLE_MAX_PIECE_JOINTE_MCP = 3 * 1024 * 1024;
/** Longueur maximale du contenu base64 correspondant (avec remplissage « = ») : 4 194 304 caractères. */
export const LONGUEUR_MAX_CONTENU_MCP = Math.ceil(TAILLE_MAX_PIECE_JOINTE_MCP / 3) * 4;

/** Champs communs à toute demande de dépôt : formulaire S3 du navigateur et outil MCP. */
const champsDepot = {
  ressource: z.string().min(1),           // clé du registre des pièces jointes (api/src/pj/objets.ts)
  objetId: z.string().min(1).max(64).optional(),
  filiale: schemaCodeFiliale.optional(),
  nom: z.string().trim().min(1).max(200),
  type: schemaTypePieceJointe,
};

/**
 * Demande de dépôt. Dépôt direct : objetId = l'objet existant. Dépôt différé (objet déclaré « differe » dans le
 * socle, fichiers consommés par le service du module à la création de l'objet) : objetId = l'objet PARENT existant
 * (ex. l'incident d'un futur commentaire) ou, sans parent (formulaire de création), la filiale de l'objet à créer.
 */
export const schemaDemandeTeleversement = z.object({
  ...champsDepot,
  differe: z.boolean().optional(),
  taille: z.number().int().min(1).max(TAILLE_MAX_PIECE_JOINTE),
}).refine((d) => (d.differe ? !!(d.objetId || d.filiale) : !!d.objetId), {
  path: ['objetId'],
  message: 'Objet (ou, pour un dépôt différé, filiale) attendu',
});

/**
 * Dépôt par un outil MCP : les champs de la demande (mêmes règles) et le contenu du fichier en base64 (RFC 4648,
 * alphabet standard, remplissage « = »), au plus TAILLE_MAX_PIECE_JOINTE_MCP octets. Le mode (direct ou différé)
 * n'est pas demandé : c'est celui que le registre déclare pour la ressource ; le service applique ensuite
 * schemaDemandeTeleversement (objet, ou filiale pour un dépôt différé sans parent).
 */
export const schemaDepotPieceJointeMcp = z.object({
  ...champsDepot,
  contenu: z.base64('Contenu attendu en base64 (RFC 4648 : alphabet standard, remplissage « = »)')
    .min(4, 'Fichier vide')
    .max(LONGUEUR_MAX_CONTENU_MCP,
      `Fichier de ${TAILLE_MAX_PIECE_JOINTE_MCP / (1024 * 1024)} Mo au plus par un outil MCP : au-delà, le déposer depuis l'écran d'AGORA`),
});

/** Identifiants de dépôts différés joints à la création d'un objet (schéma de création du module). */
export const schemaIdsDepots = (max: number) =>
  z.array(z.guid()).max(max).refine((ids) => new Set(ids).size === ids.length, 'Une même pièce jointe est annoncée deux fois');

export const schemaTeleversement = z.object({
  id: z.string(),
  url: z.string(),                        // URL du dépôt S3 direct (formulaire POST)
  champs: z.record(z.string(), z.string()), // champs du formulaire POST à envoyer avant le fichier
  expireLe: schemaDateIso,
});

export const schemaPieceJointe = z.object({
  id: z.string(),
  filiale: schemaCodeFiliale,
  ressource: z.string(),
  objetId: z.string(),
  nom: z.string(),
  type: schemaTypePieceJointe,
  taille: z.number().int(),
  sha256: z.string(),
  deposeLe: schemaDateIso,
  deposePar: z.string(),
});

/**
 * Résultat d'un dépôt par un outil MCP : la pièce jointe enregistrée (dépôt direct) ou le dépôt temporaire, dont l'`id`
 * se passe dans `piecesJointes` à l'outil qui crée l'objet (dépôt différé, dans la journée : tmp/ purgé après 1 jour).
 */
export const schemaDepotPieceJointeMcpCree = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('direct'), pieceJointe: schemaPieceJointe }),
  z.object({
    mode: z.literal('differe'),
    depot: z.object({ id: z.string(), nom: z.string(), type: schemaTypePieceJointe, taille: z.number().int() }),
  }),
]);

export const schemaLienTelechargement = z.object({ url: z.string(), expireLe: schemaDateIso });

// ------------------------------------------------------------------ historique d'un objet (journal d'audit)
/**
 * Historique d'un objet lu dans core.audit (api/src/core/historique.ts) : écritures seulement – jamais les lectures ni
 * les refus –, sans valeurs ni texte libre (noms des champs modifiés), du plus récent au plus ancien.
 */
export const ACTIONS_HISTORIQUE = ['creer', 'modifier', 'supprimer', 'valider'] as const;

export const schemaEvenementHistorique = z.object({
  le: schemaDateIso,
  auteur: z.string().nullable(),          // e-mail de l'auteur ; null si le métier masque les auteurs (sauf soi-même)
  action: z.enum(ACTIONS_HISTORIQUE),
  evenement: z.string(),                  // évènement du métier (declare, qualifie…, piece-jointe-ajoutee) ou l'action
  champs: z.array(z.string()),            // champs modifiés, sans leurs valeurs
});

export const schemaRequeteHistorique = z.object({
  limite: z.coerce.number().int().min(1).max(200).default(100),
  suivant: z.string().optional(),
});
